import React, {useEffect} from "react";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import {useMediaQuery} from "react-responsive";

import {BaseModal} from "../../components/BaseModal";
import {mediaQueryForMobile} from "../HomePage/HomePage";
import {useThemeStore} from "../../stores/themeStore";
import {useListingsStore} from "../../stores/listingsStore";
import {useSavedDealershipsStore} from "../../stores/savedDealershipsStore";
import {ListingsSelectionContext} from "../../contexts/ListingsSelectionContext";

import {SearchBar} from "./SearchBar";
import {ActiveFiltersBar} from "./ActiveFiltersBar";
import {Sidebar} from "./Sidebar";
import {MainContent} from "./MainContent";
import {SavedDealershipsPanel} from "./SavedDealershipsPanel";
import {seedFiltersForVehicle, seedLocation, standaloneListingsUrl} from "./utils";

import "./ListingsPage.scss";
import "./ListingsPageModal.scss";

// BaseModal wrapper stays transparent; the .ListingsPageModal card sizes the
// (near-fullscreen) surface and #ListingsPage paints its own themed background.
const wrapperStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    background: 'transparent',
    boxShadow: 'none',
    outline: 'none',
    padding: 0,
};

// The full ListingsPage as a modal, openable from a DealPage. `seed` pre-filters
// by the deal's vehicle; `onSelectListing({msrp, vin, isUsed, vehicle})` is
// invoked when a card's Select is clicked (then the modal closes).
export const ListingsPageModal = ( { seed, onSelectListing, isOpen, setIsOpen } ) => {
    const theme = useThemeStore((state) => state.theme)
    const isTabletOrMobile = useMediaQuery({query: mediaQueryForMobile})
    const setFilters = useListingsStore((state) => state.setFilters)
    const fetchSaved = useSavedDealershipsStore((state) => state.fetchSaved)

    // Seed the shared listings store from the deal's vehicle on open. Load saved
    // dealerships first so the single seed fetch already carries the dealer filter.
    useEffect(() => {
        (async () => {
            await fetchSaved()

            // Model-level search (no trim): make/model become the search tags, the
            // year the Year multi-select, and New/Used is preselected from the
            // deal's vehicle. Opening from a deal is a fresh vehicle search, so the
            // payload also resets the sidebar facets left over from whatever was
            // searched before — see seedFiltersForVehicle.
            const newFilters = seedFiltersForVehicle(seed)
            if (seed?.zipcode) {
                newFilters.location = seedLocation(
                    useListingsStore.getState().filters.location,
                    seed.zipcode,
                )
            }
            setFilters(newFilters, true)
        })()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const contextValue = {
        onSelectListing: (data) => {
            onSelectListing?.(data)
            setIsOpen(null)
        },
    }

    return (
        <BaseModal isOpen={isOpen} setIsOpen={setIsOpen} style={wrapperStyle}>
            <ListingsSelectionContext.Provider value={contextValue}>
                <div className="ListingsPageModal" data-theme={theme}>
                    {
                        // Desktop only — below 1224px the search bar reclaims this
                        // corner (see ListingsPageModal.scss).
                        !isTabletOrMobile &&
                        <a
                            className="ListingsPageModal__newtab"
                            href={standaloneListingsUrl(seed)}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <OpenInNewOutlinedIcon/>
                            <span>Open in new tab</span>
                        </a>
                    }
                    <div className="ListingsPageModal__body">
                        <div id="ListingsPage" data-theme={theme}>
                            <SearchBar/>
                            <ActiveFiltersBar/>
                            <div className="layout">
                                <Sidebar/>
                                <MainContent/>
                                <SavedDealershipsPanel/>
                            </div>
                        </div>
                    </div>
                </div>
            </ListingsSelectionContext.Provider>
        </BaseModal>
    )
}
